import * as APITypes from "../API";
type GeneratedQuery<InputType, OutputType> = string & {
  __generatedQueryInput: InputType;
  __generatedQueryOutput: OutputType;
};

export const getAccount = /* GraphQL */ `
  query CustomGetAccount($id: ID!) {
    getAccount(id: $id) {
      id
      gid
      name
      accountType {
        levelName
      }
      mfaEnabled
      createdAt
      updatedAt
    }
  }
` as GeneratedQuery<
  APITypes.CustomGetAccountQueryVariables,
  APITypes.CustomGetAccountQuery
>;

export const customGetUser = /* GraphQL */ `
  query CustomGetUser(
    $id: ID!
    $accountsLimit: Int
    $accountsNextToken: String
  ) {
    getUser(id: $id) {
      id
      email
      name
      picture
      createdAt
      updatedAt
      accounts(limit: $accountsLimit, nextToken: $accountsNextToken) {
        items {
          id
          accountId
          userId
          createdAt
          updatedAt
          permissions
          account {
            id
            gid
            name
            mfaEnabled
            createdAt
            updatedAt
            tierId
            accountType {
              levelName
            }
            fonts {
              fontFamily
              variants {
                name
                weight
                style
                url
              }
            }
          }
        }
        nextToken
      }
    }
  }
` as GeneratedQuery<
  APITypes.CustomGetUserQueryVariables,
  APITypes.CustomGetUserQuery
>;

export const customGetProject = /* GraphQL */ `
  query CustomGetProject($id: ID!) {
    getProject(id: $id) {
      id
      projectAccountId
      title
      account {
        id
        gid
        name
        aliasId
        minisiteDomain
        mfaEnabled
        tierId
        createNewVideosWithBlingLogo
        createdAt
        updatedAt
      }
      videoParts {
        name
        jsonUrl
        mods
        modsArr {
          id
          dataStr
          origin
          name
          description
        }
        updatedAt
        hasVideo
        origin
        playerVersionToUse
        description
        fonts {
          family
          style
          url
          weight
        }
      }
      allowCreateDataPoint
      allowDataConnect
      allowCrmConnect
      allowSdkConnect
      allowSendFormData
      analyticsReportUrl
      createdAt
      dataFiles {
        fileName
        tag
        createdAt
      }
      description
      fileName
      jsonChanges
      jsonVidUrl
      playerSettings {
        autoplay
        autoplay_delay
        posterFrame
        showTimeline
        storiesMode
        color_loader
        color_ctrlBtns
        color_rail
        color_progress
        color_thumb
        color_bg
        muted
        autoReplay
        showBlingsLogo
        blingsLogoColor
        loadingText
        loadingImage
      }
      projectVersions {
        nextToken
      }
      settings
      stateJsonSchemaStr
      settingsJsonSchemaStr
      thumbS3Url
      experiments {
        id
        type
        variants {
          id
          value
          successFactors {
            conversionSuccessData
            engagementSuccessData
            selectionCount
            watchTimeSuccessData
          }
        }
      }
      experimentOptimizationTechnique
      analyticsEnabled
      owner
      aliasId
      minisiteConfigs {
        scenes
        title
        description
        thumbUrl
        faviconUrl
        preCreate
      }
      playerVersionToUse
      customHtml
      dynamicThumbnails {
        name
        id
        createdAt
        updatedAt
      }
      publishedAt
      allowedOrigins
      flowDiagram {
        nodes {
          id
          label
          data
          lineupId
          type
        }
        edges {
          source
          target
          sourceHandle
        }
      }
      textToVideoScript
      instantiatedFromUseCase
      overlay {
        audio {
          url
          volume
          loop
        }
      }
      fontOverrides {
        family
        url
        weight
        style
      }
      isDeleted
      updatedAt
    }
  }
` as GeneratedQuery<
  APITypes.GetProjectQueryVariables,
  APITypes.GetProjectQuery
>;

type GeneratedMutation<InputType, OutputType> = string & {
  __generatedMutationInput: InputType;
  __generatedMutationOutput: OutputType;
};

// Hand-written replacement for the insertIntoGraphQlString-widened
// getProjectVersion query in projectTools.ts: same effective selection, plus
// nodeLayout (whose entries are keyed by flow node id and must follow node-id
// renames).
export const customGetProjectVersion = /* GraphQL */ `
  query CustomGetProjectVersion($id: ID!) {
    getProjectVersion(id: $id) {
      id
      accountOwner
      owner
      stateJsonSchemaStr
      settingsJsonSchemaStr
      settings
      videoParts {
        name
        jsonUrl
        mods
        updatedAt
        hasVideo
        origin
        playerVersionToUse
        description
        modsArr {
          id
          dataStr
          origin
          name
          description
        }
        fonts {
          family
          style
          url
          weight
        }
      }
      updatedAt
      playerVersionToUse
      experiments {
        id
        type
        variants {
          id
          value
          successFactors {
            conversionSuccessData
            engagementSuccessData
            selectionCount
            watchTimeSuccessData
          }
        }
      }
      experimentOptimizationTechnique
      textToVideoScript
      flowDiagram {
        nodes {
          id
          label
          data
          lineupId
          type
        }
        edges {
          source
          target
          sourceHandle
        }
      }
      nodeLayout {
        entries {
          nodeId
          position {
            x
            y
          }
        }
      }
      fontOverrides {
        family
        url
        weight
        style
      }
      playerSettings {
        autoplay
        autoplay_delay
        posterFrame
        showTimeline
        storiesMode
        color_loader
        color_ctrlBtns
        color_rail
        color_progress
        color_thumb
        color_bg
        muted
        autoReplay
        showBlingsLogo
        blingsLogoColor
        loadingText
        loadingImage
      }
      fontMap {
        slot
      }
      createdAt
      projectProjectVersionsId
    }
  }
` as GeneratedQuery<
  APITypes.GetProjectVersionQueryVariables,
  APITypes.GetProjectVersionQuery
>;

// Minimal-response mutation for coordinated draft writes of flowDiagram and
// videoParts (node-id normalization, scene rename/creation/duplication).
export const customUpdateProjectVersionNodeIds = /* GraphQL */ `
  mutation CustomUpdateProjectVersionNodeIds($input: UpdateProjectVersionInput!) {
    updateProjectVersion(input: $input) {
      id
      updatedAt
    }
  }
` as GeneratedMutation<
  APITypes.UpdateProjectVersionMutationVariables,
  APITypes.UpdateProjectVersionMutation
>;
